if (!customElements.get('build-whey-sachet-pack')) {
  customElements.define(
    'build-whey-sachet-pack',
    class BuildWheySachetPack extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onSummaryToggle = this.toggleSummary.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentSubmit = this.onDocumentSubmit.bind(this);
        this.onKeyIngredientsMediaChange = this.positionKeyIngredients.bind(this);
      }

      connectedCallback() {
        this.min = Number(this.dataset.min || 5);
        this.cards = Array.from(this.querySelectorAll('[data-sachet-card]'));
        this.totalElement = this.querySelector('[data-sachet-total]');
        this.statusElement = this.querySelector('[data-sachet-status]');
        this.subtotalElement = this.querySelector('[data-sachet-subtotal]');
        this.progressElement = this.querySelector('[data-sachet-progress]');
        this.selectedElement = this.querySelector('[data-sachet-selected]');
        this.summaryElement = this.querySelector('.build-whey-sachet-pack__summary');
        this.summaryToggle = this.querySelector('[data-sachet-summary-toggle]');
        this.summaryToggleText = this.querySelector('[data-sachet-summary-toggle-text]');
        this.summaryIcon = this.querySelector('[data-sachet-summary-icon]');
        this.submitButton = this.querySelector('[data-sachet-submit]');
        this.submitText = this.querySelector('[data-sachet-submit-text]');
        this.errorElement = this.querySelector('[data-sachet-error]');
        this.layoutElement = this.querySelector('.build-whey-sachet-pack__layout');
        this.currency = this.dataset.currency || 'INR';
        this.parentVariantId = Number(this.dataset.parentVariantId || 0);

        this.addEventListener('click', this.onClick);
        document.addEventListener('click', this.onDocumentClick, true);
        document.addEventListener('submit', this.onDocumentSubmit, true);
        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.onSubmit);
        }
        if (this.summaryToggle) {
          this.syncSummaryToggle();
          this.summaryToggle.addEventListener('click', this.onSummaryToggle);
        }

        this.setupMobileKeyIngredients();
        this.removeParentPackLine();
        this.update();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('click', this.onDocumentClick, true);
        document.removeEventListener('submit', this.onDocumentSubmit, true);
        if (this.submitButton) {
          this.submitButton.removeEventListener('click', this.onSubmit);
        }
        if (this.summaryToggle) {
          this.summaryToggle.removeEventListener('click', this.onSummaryToggle);
        }
        this.teardownMobileKeyIngredients();
      }

      onDocumentClick(event) {
        const blockedButton = event.target.closest(
          '#flo-buy-now-button, [name="add"], [type="submit"], .shopify-payment-button__button'
        );
        if (!blockedButton || this.contains(blockedButton)) return;

        const form = blockedButton.closest('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
        if (form && !this.formContainsParentVariant(form)) return;

        if (!form && blockedButton.id !== 'flo-buy-now-button') return;

        this.blockParentPackAdd(event);
      }

      onDocumentSubmit(event) {
        const form = event.target;
        if (!form || this.contains(form) || !this.formContainsParentVariant(form)) return;

        this.blockParentPackAdd(event);
      }

      blockParentPackAdd(event) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }

        this.showError(`Please choose at least ${this.min} sachets before checkout.`);
        this.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }

      formContainsParentVariant(form) {
        if (!this.parentVariantId || !form) return false;

        const variantInput = form.querySelector('[name="id"]');
        return variantInput && Number(variantInput.value) === this.parentVariantId;
      }

      onClick(event) {
        const minusButton = event.target.closest('[data-sachet-minus]');
        const plusButton = event.target.closest('[data-sachet-plus]');
        if (!minusButton && !plusButton) return;

        const card = event.target.closest('[data-sachet-card]');
        if (!card || card.dataset.available !== 'true') return;

        const input = card.querySelector('[data-sachet-quantity]');
        if (!input) return;

        const currentQuantity = Number(input.value || 0);
        const nextQuantity = plusButton ? currentQuantity + 1 : currentQuantity - 1;
        this.setQuantity(card, nextQuantity);
        this.update();
      }

      setQuantity(card, quantity) {
        const input = card.querySelector('[data-sachet-quantity]');
        if (!input) return;

        const max = card.dataset.max ? Number(card.dataset.max) : null;
        const clampedQuantity = Math.max(0, max === null ? quantity : Math.min(quantity, max));
        input.value = String(clampedQuantity);
        this.hideError();
      }

      update() {
        let total = 0;
        let subtotal = 0;
        const selectedItems = [];

        this.cards.forEach((card) => {
          const input = card.querySelector('[data-sachet-quantity]');
          const quantity = input ? Number(input.value || 0) : 0;
          const price = Number(card.dataset.price || 0);
          const max = card.dataset.max ? Number(card.dataset.max) : null;
          const minusButton = card.querySelector('[data-sachet-minus]');
          const plusButton = card.querySelector('[data-sachet-plus]');

          total += quantity;
          subtotal += quantity * price;
          card.classList.toggle('is-selected', quantity > 0);

          if (quantity > 0) {
            selectedItems.push({
              title: card.dataset.title || 'Sachet',
              quantity,
            });
          }

          if (minusButton) minusButton.disabled = quantity <= 0;
          if (plusButton) plusButton.disabled = card.dataset.available !== 'true' || (max !== null && quantity >= max);
        });

        const remaining = Math.max(this.min - total, 0);
        const canSubmit = remaining === 0 && total > 0;

        if (this.totalElement) this.totalElement.textContent = String(total);
        if (this.subtotalElement) this.subtotalElement.textContent = this.formatMoney(subtotal);
        if (this.statusElement) {
          this.statusElement.textContent = canSubmit
            ? `${total} sachets selected`
            : `Choose ${remaining} more sachet${remaining === 1 ? '' : 's'} to continue`;
        }
        if (this.progressElement) {
          this.progressElement.style.width = `${Math.min((total / this.min) * 100, 100)}%`;
        }
        this.renderSelectedItems(selectedItems);
        if (this.submitButton) this.submitButton.disabled = !canSubmit || this.submitButton.classList.contains('is-loading');
        if (this.submitText) this.submitText.textContent = canSubmit ? 'Add sachet pack to cart' : `Select ${remaining} more`;
        if (this.summaryToggleText) this.summaryToggleText.textContent = total > 0 ? 'Review box' : 'Box is empty';

      }

      toggleSummary() {
        if (!this.summaryElement || !this.summaryToggle) return;

        const isExpanded = this.summaryElement.classList.toggle('is-expanded');
        this.syncSummaryToggle(isExpanded);
      }

      syncSummaryToggle(isExpanded = null) {
        if (!this.summaryElement || !this.summaryToggle) return;

        const expanded = isExpanded === null ? this.summaryElement.classList.contains('is-expanded') : isExpanded;
        this.summaryToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (this.summaryIcon) this.summaryIcon.textContent = expanded ? '-' : '+';
      }

      setupMobileKeyIngredients() {
        this.keyIngredientsSection = document.querySelector('[id$="__key_ingredients"]');
        if (!this.layoutElement || !this.keyIngredientsSection || !this.keyIngredientsSection.parentNode) return;

        this.keyIngredientsPlaceholder = document.createComment('build-whey-sachet-pack key ingredients position');
        this.keyIngredientsSection.parentNode.insertBefore(this.keyIngredientsPlaceholder, this.keyIngredientsSection);
        this.keyIngredientsMediaQuery = window.matchMedia('(max-width: 749px)');

        if (this.keyIngredientsMediaQuery.addEventListener) {
          this.keyIngredientsMediaQuery.addEventListener('change', this.onKeyIngredientsMediaChange);
        } else if (this.keyIngredientsMediaQuery.addListener) {
          this.keyIngredientsMediaQuery.addListener(this.onKeyIngredientsMediaChange);
        }

        this.positionKeyIngredients();
      }

      teardownMobileKeyIngredients() {
        if (this.keyIngredientsMediaQuery) {
          if (this.keyIngredientsMediaQuery.removeEventListener) {
            this.keyIngredientsMediaQuery.removeEventListener('change', this.onKeyIngredientsMediaChange);
          } else if (this.keyIngredientsMediaQuery.removeListener) {
            this.keyIngredientsMediaQuery.removeListener(this.onKeyIngredientsMediaChange);
          }
        }

        this.restoreKeyIngredients();

        if (this.keyIngredientsPlaceholder && this.keyIngredientsPlaceholder.parentNode) {
          this.keyIngredientsPlaceholder.parentNode.removeChild(this.keyIngredientsPlaceholder);
        }
      }

      positionKeyIngredients() {
        if (!this.keyIngredientsSection || !this.layoutElement || !this.keyIngredientsMediaQuery) return;

        if (this.keyIngredientsMediaQuery.matches) {
          this.keyIngredientsSection.classList.add('build-whey-sachet-pack__mobile-key-ingredients');
          this.insertBefore(this.keyIngredientsSection, this.layoutElement);
        } else {
          this.restoreKeyIngredients();
        }
      }

      restoreKeyIngredients() {
        if (!this.keyIngredientsSection || !this.keyIngredientsPlaceholder || !this.keyIngredientsPlaceholder.parentNode) return;

        this.keyIngredientsSection.classList.remove('build-whey-sachet-pack__mobile-key-ingredients');
        this.keyIngredientsPlaceholder.parentNode.insertBefore(
          this.keyIngredientsSection,
          this.keyIngredientsPlaceholder.nextSibling
        );
      }

      renderSelectedItems(items) {
        if (!this.selectedElement) return;

        if (!items.length) {
          this.selectedElement.innerHTML = '<span class="build-whey-sachet-pack__selected-empty">No sachets selected yet</span>';
          return;
        }

        this.selectedElement.innerHTML = items
          .map((item) => `<span><strong>${item.quantity}x</strong> ${this.escapeHtml(item.title)}</span>`)
          .join('');
      }

      getSelectedItems() {
        return this.cards
          .map((card) => {
            const input = card.querySelector('[data-sachet-quantity]');
            return {
              id: Number(card.dataset.variantId),
              quantity: input ? Number(input.value || 0) : 0,
            };
          })
          .filter((item) => item.id && item.quantity > 0);
      }

      async getCart() {
        const cartUrl = this.dataset.cartUrl || '/cart';
        const response = await fetch(`${cartUrl}.js`, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) throw new Error('Unable to read cart.');

        return response.json();
      }

      async removeParentPackLine(selectedVariantIds = []) {
        if (!this.parentVariantId || !this.dataset.cartChangeUrl) return;

        const selectedIds = selectedVariantIds.map((id) => Number(id));

        try {
          let cart = await this.getCart();
          const parentLines = (cart.items || []).filter(
            (item) => Number(item.variant_id) === this.parentVariantId && !selectedIds.includes(Number(item.variant_id))
          );

          for (const line of parentLines) {
            const response = await fetch(this.dataset.cartChangeUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({
                id: line.key,
                quantity: 0,
              }),
            });

            if (response.ok) {
              cart = await response.json();
            }
          }

          return cart;
        } catch (error) {
          console.warn('Unable to remove empty sachet pack parent product from cart.', error);
        }
      }

      async onSubmit(event) {
        event.preventDefault();

        const items = this.getSelectedItems();
        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

        if (totalQuantity < this.min) {
          this.update();
          return;
        }

        this.setLoading(true);

        try {
          const response = await fetch(this.dataset.cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: items.map((item) => ({
                id: item.id,
                quantity: item.quantity,
                properties: {
                  Pack: this.dataset.packTitle || 'Build Your Own Whey Sachet Pack',
                },
              })),
            }),
          });

          const cartData = await response.json();

          if (!response.ok || cartData.status) {
            throw new Error(cartData.description || cartData.message || 'Unable to add sachets to cart.');
          }

          const cleanedCartData = await this.removeParentPackLine(items.map((item) => item.id));

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'build-whey-sachet-pack',
              cartData: cleanedCartData || cartData,
            });
          }

          if (typeof window.handleFloCartBtn === 'function') {
            window.handleFloCartBtn();
          } else {
            window.location.href = this.dataset.cartUrl || '/cart';
          }
        } catch (error) {
          this.showError(error.message || 'Unable to add sachets to cart. Please try again.');
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;

        this.submitButton.classList.toggle('is-loading', isLoading);
        this.submitButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        this.update();
      }

      showError(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }

      hideError() {
        if (!this.errorElement) return;
        this.errorElement.hidden = true;
        this.errorElement.textContent = '';
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
            style: 'currency',
            currency: this.currency,
            maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
          }).format(cents / 100);
        } catch (error) {
          return `${(cents / 100).toFixed(2)}`;
        }
      }

      escapeHtml(value) {
        return String(value).replace(/[&<>"']/g, (character) => {
          const entities = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;',
          };
          return entities[character];
        });
      }
    }
  );
}
